// frontend/src/screens/admin/sps/StaffDirectoryListScreen.jsx
import { useState, useRef, useCallback } from 'react';
import {
  Container, Row, Col, Table, Button, Badge, Form,
  Alert, Spinner, Pagination, InputGroup, Modal, ProgressBar,
} from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import {
  useGetStaffDirectoryQuery,
  useSyncBatchMutation,
} from '../../../slices/spsApiSlices';

const BATCH_SIZE = 50;

const INITIAL_SYNC = {
  status:   'idle',
  total:    0,
  processed:0,
  created:  0,
  updated:  0,
  errors:   0,
  pct:      0,
  message:  '',
};

const schemeBadge = (m) => m.isSingleScheme
  ? <Badge bg="primary"   style={{ fontSize: '.62rem' }}>SPS</Badge>
  : <Badge bg="light" text="dark" style={{ fontSize: '.62rem', border: '1px solid #ccc' }}>Non-SPS</Badge>;

const statusBadge = (m) => {
  if (!m.isActive) return <Badge bg="secondary">Leaver</Badge>;
  if (!m.email)    return <Badge bg="warning" text="dark">No email</Badge>;
  return <Badge bg="success">Active</Badge>;
};

const emailBadge = (m) => {
  if (!m.lastEmailSent) return <span className="text-muted small">—</span>;
  return (
    <span>
      <span className="small">{new Date(m.lastEmailSent).toLocaleDateString('en-IE')}</span>{' '}
      {m.lastEmailStatus === 'sent'   && <Badge bg="success" style={{ fontSize: '.62rem' }}>sent</Badge>}
      {m.lastEmailStatus === 'failed' && <Badge bg="danger"  style={{ fontSize: '.62rem' }}>failed</Badge>}
    </span>
  );
};

function SyncProgress({ sync }) {
  const { status, total, processed, created, updated, errors, pct } = sync;
  if (status === 'idle') return null;

  const isRunning = status === 'loading' || status === 'running';
  const isDone    = status === 'done';
  const isError   = status === 'error';

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-baseline mb-1">
        <span className="small fw-semibold" style={{
          color: isError ? '#dc3545' : isDone ? '#1a6e3c' : '#1e3a5f'
        }}>
          {status === 'loading'  && '📋 Loading staff list from pay data…'}
          {status === 'running'  && '⟳ Syncing staff directory…'}
          {isDone                && '✓ Sync complete'}
          {isError               && '✗ Sync failed'}
        </span>
        {total > 0 && (
          <span style={{ fontFamily: 'monospace', fontSize: '.85rem', fontWeight: 700 }}>
            <span style={{ color: isDone ? '#1a6e3c' : '#0d6efd' }}>{processed.toLocaleString()}</span>
            <span style={{ color: '#888', fontWeight: 400 }}> / {total.toLocaleString()}</span>
          </span>
        )}
      </div>

      <ProgressBar
        animated={isRunning}
        now={status === 'loading' ? 100 : pct}
        variant={isError ? 'danger' : isDone ? 'success' : 'primary'}
        striped={status === 'loading'}
        style={{ height: 24, borderRadius: 4 }}
        label={!isRunning || pct > 8 ? (status === 'loading' ? '' : `${pct}%`) : ''}
      />

      {(status === 'running' || isDone) && total > 0 && (
        <Row className="g-2 mt-2">
          {[
            ['Processed', processed, 'secondary'],
            ['Created',   created,   'success'],
            ['Updated',   updated,   'primary'],
            ['Errors',    errors,    errors > 0 ? 'danger' : 'secondary'],
          ].map(([label, val, col]) => (
            <Col key={label}>
              <div className="text-center">
                <div className={`fw-bold text-${col}`}
                  style={{ fontSize: '1.05rem', fontFamily: 'monospace' }}>
                  {val.toLocaleString()}
                </div>
                <div className="text-muted" style={{ fontSize: '.7rem' }}>{label}</div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {isError && sync.message && (
        <Alert variant="danger" className="mt-2 mb-0 py-2" style={{ fontSize: '.85rem' }}>
          {sync.message}
        </Alert>
      )}
    </div>
  );
}

export default function StaffDirectoryListScreen() {
  const [search,         setSearch]         = useState('');
  const [isActive,       setIsActive]       = useState('');
  const [hasEmail,       setHasEmail]       = useState('');
  const [isSingleScheme, setIsSingleScheme] = useState('true');
  const [sendEmail,      setSendEmail]      = useState('');
  const [page,           setPage]           = useState(1);
  const [showSync,       setShowSync]       = useState(false);
  const [sync,           setSync]           = useState(INITIAL_SYNC);

  const [syncBatch] = useSyncBatchMutation();
  const abortRef    = useRef(false);

  const queryParams = {
    page, limit: 50,
    ...(search         && { search }),
    ...(isActive       && { isActive }),
    ...(hasEmail       && { hasEmail }),
    ...(isSingleScheme && { isSingleScheme }),
    ...(sendEmail      && { sendEmail }),
  };

  const { data, isLoading, isFetching, isError, refetch } = useGetStaffDirectoryQuery(queryParams);

  const staff = data?.staff || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const applyFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };

  // ── Export current filtered results to CSV ──────────────────────────────────
  const exportCSV = async () => {
    // Fetch all matching records (no pagination limit)
    const params = new URLSearchParams({
      page: 1, export: 'true',
      ...(search         && { search }),
      ...(isActive       && { isActive }),
      ...(hasEmail       && { hasEmail }),
      ...(isSingleScheme && { isSingleScheme }),
      ...(sendEmail      && { sendEmail }),
    });
    try {
      const resp = await fetch(`/api/staff-directory?${params}`, { credentials: 'include' });
      const json = await resp.json();
      const rows = json.staff || [];

      const headers = ['PPS Number','Name','Email','Send Email','Payment Freq',
                       'Scheme Start','Last Pay Year','Last Cert Year',
                       'Last Email Sent','Scheme','Status'];
      const lines = [
        headers.join(','),
        ...rows.map(m => [
          m.ppsNumber,
          `"${(m.employeeName||'').replace(/"/g,'""')}"`,
          m.email || '',
          m.sendEmail === false ? 'No' : 'Yes',
          m.paymentFrequency || '',
          m.schemeStartYear || '',
          m.lastPayYear || '',
          m.lastStatementYear || '',
          m.lastEmailSent ? new Date(m.lastEmailSent).toLocaleDateString('en-IE') : '',
          m.isSingleScheme ? 'SPS' : 'Non-SPS',
          !m.isActive ? 'Leaver' : !m.email ? 'No email' : 'Active',
        ].join(','))
      ].join('\n');

      const blob = new Blob([lines], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `staff-directory-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const runSync = useCallback(async () => {
    abortRef.current = false;
    setSync({ ...INITIAL_SYNC, status: 'loading' });

    const csrf = document.cookie
      .split('; ')
      .find(r => r.startsWith('csrf-token='))
      ?.split('=')[1] || '';

    let records;
    try {
      const resp = await fetch('/api/staff-directory/sync-summary', {
        headers: { 'x-csrf-token': csrf },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      records = data.records;
    } catch (err) {
      setSync(s => ({ ...s, status: 'error', message: `Failed to load pay data: ${err.message}` }));
      return;
    }

    const total        = records.length;
    const totalBatches = Math.ceil(total / BATCH_SIZE);
    let processed = 0, created = 0, updated = 0, errors = 0;

    setSync(s => ({ ...s, status: 'running', total, processed: 0, pct: 0 }));

    for (let i = 0; i < totalBatches; i++) {
      if (abortRef.current) break;

      const batch = records.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);

      try {
        const result = await syncBatch({ batch }).unwrap();
        created   += result.created || 0;
        updated   += result.updated || 0;
        errors    += result.errors  || 0;
      } catch (err) {
        errors++;
      }

      processed = Math.min((i + 1) * BATCH_SIZE, total);
      const pct = Math.round((processed / total) * 100);

      setSync({ status: 'running', total, processed, pct, created, updated, errors, message: '' });
    }

    setSync(s => ({
      ...s,
      status:    abortRef.current ? 'idle' : 'done',
      pct:       100,
      processed: total,
    }));

    if (!abortRef.current) refetch();
  }, [syncBatch, refetch]);

  const openSync  = () => { setSync(INITIAL_SYNC); setShowSync(true); };
  const closeSync = () => {
    abortRef.current = true;
    setShowSync(false);
    setSync(INITIAL_SYNC);
  };

  const isDone    = sync.status === 'done';
  const isRunning = sync.status === 'loading' || sync.status === 'running';

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Staff Directory</h4>
          <small className="text-muted">
            Showing <strong>{total.toLocaleString()}</strong> member{total !== 1 ? 's' : ''} — click a row to view and edit details
          </small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-success" size="sm" onClick={exportCSV}>
            ↓ Export CSV
          </Button>
          <Button variant="outline-primary" size="sm" onClick={openSync}>
            ⟳ Sync from Pay Data
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="g-2 mb-3">
        <Col xs={12} sm={5} md={4}>
          <InputGroup size="sm">
            <InputGroup.Text>🔍</InputGroup.Text>
            <Form.Control
              placeholder="Search name or PPS…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            {search && (
              <Button variant="outline-secondary" onClick={() => { setSearch(''); setPage(1); }}>✕</Button>
            )}
          </InputGroup>
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={isActive} onChange={applyFilter(setIsActive)}>
            <option value="">Active &amp; Leavers</option>
            <option value="true">Active only</option>
            <option value="false">Leavers only</option>
          </Form.Select>
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={hasEmail} onChange={applyFilter(setHasEmail)}>
            <option value="">All email status</option>
            <option value="true">Has email address</option>
            <option value="false">No email address</option>
          </Form.Select>
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={isSingleScheme} onChange={applyFilter(setIsSingleScheme)}>
            <option value="true">Single Scheme only</option>
            <option value="">All staff</option>
            <option value="false">Non-scheme staff</option>
          </Form.Select>
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={sendEmail} onChange={applyFilter(setSendEmail)}>
            <option value="">All send email</option>
            <option value="true">Send email on</option>
            <option value="false">Send email off</option>
          </Form.Select>
        </Col>
      </Row>

      {isError && <Alert variant="danger">Failed to load staff directory.</Alert>}

      {/* Totals summary bar */}
      {!isLoading && total > 0 && (
        <div className="d-flex gap-3 mb-2 px-1 flex-wrap align-items-center" style={{ fontSize: '.82rem', color: '#444' }}>
          <span>
            <strong>{total.toLocaleString()}</strong>{' '}
            record{total !== 1 ? 's' : ''} matching current filters
          </span>
          <span className="text-muted" style={{ marginLeft: 'auto', fontStyle: 'italic' }}>
            Page {page} of {pages} &nbsp;·&nbsp; {Math.min(50, staff.length)} shown per page
          </span>
        </div>
      )}

      <div className="table-responsive" style={{ position: 'relative' }}>
        {(isLoading || isFetching) && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.7)', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner animation="border" />
          </div>
        )}
        <Table bordered hover size="sm" className="mb-0" style={{ fontSize: '.82rem' }}>
          <thead className="table-dark">
            <tr>
              <th>PPS Number</th>
              <th>Name</th>
              <th>Email</th>
              <th>Freq</th>
              <th>Scheme yrs</th>
              <th>Last cert</th>
              <th>Last email</th>
              <th>Scheme</th>
              <th>Status</th>
              <th style={{ width: 70 }}>Open</th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && !isLoading && (
              <tr><td colSpan={10} className="text-center text-muted py-4">No staff found matching your filter</td></tr>
            )}
            {staff.map((m) => (
              <tr key={m.ppsNumber}>
                <td style={{ fontFamily: 'monospace', fontSize: '.78rem' }}>{m.ppsNumber}</td>
                <td className="fw-semibold">{m.employeeName}</td>
                <td style={{ fontSize: '.78rem' }}>
                  {m.email || <span className="text-danger fst-italic">no email</span>}
                </td>
                <td>
                  {m.paymentFrequency && (
                    <Badge
                      bg={m.paymentFrequency === 'Monthly' ? 'primary' : m.paymentFrequency === 'Mixed' ? 'warning' : 'success'}
                      text={m.paymentFrequency === 'Mixed' ? 'dark' : undefined}
                      style={{ fontSize: '.65rem' }}
                    >
                      {m.paymentFrequency === 'Monthly' ? 'M' : m.paymentFrequency === 'Fortnightly' ? 'FN' : 'Mix'}
                    </Badge>
                  )}
                </td>
                <td className="text-center">
                  {m.schemeStartYear && m.lastPayYear
                    ? `${m.schemeStartYear}–${m.lastPayYear}` : '—'}
                </td>
                <td className="text-center">
                  {m.lastStatementYear
                    ? <span>{m.lastStatementYear}{' '}
                        <span className="text-muted" style={{ fontSize: '.7rem' }}>
                          {m.lastCertGeneratedAt ? new Date(m.lastCertGeneratedAt).toLocaleDateString('en-IE') : ''}
                        </span>
                      </span>
                    : <span className="text-muted">—</span>}
                </td>
                <td>{emailBadge(m)}</td>
                <td>{schemeBadge(m)}</td>
                <td>{statusBadge(m)}</td>
                <td>
                  <LinkContainer to={`/admin/sps/staff/${m.ppsNumber}`}>
                    <Button size="sm" variant="outline-primary" style={{ fontSize: '.72rem', padding: '2px 8px' }}>
                      Open →
                    </Button>
                  </LinkContainer>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination size="sm">
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
            {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
              <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
            ))}
            <Pagination.Next disabled={page === pages} onClick={() => setPage(page + 1)} />
          </Pagination>
        </div>
      )}

      {/* Sync modal */}
      <Modal show={showSync} onHide={closeSync} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title>Sync Staff Directory from Pay Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sync.status === 'idle' && (
            <>
              <p>This will scan all pay records in MongoDB and for each PPS number:</p>
              <ul className="mb-3">
                <li>Create a directory entry if one doesn't exist yet</li>
                <li>Update <strong>scheme start year</strong>, <strong>last pay year</strong>, <strong>payment frequency</strong>, and <strong>isSingleScheme</strong> flag</li>
                <li>Will <strong>not</strong> overwrite manually entered fields (address, email, DOB etc.)</li>
              </ul>
              <Alert variant="info" className="mb-0">
                Progress is shown batch by batch — 50 staff members at a time.
                For 9,059 staff this takes around 30–60 seconds.
              </Alert>
            </>
          )}
          <SyncProgress sync={sync} />
        </Modal.Body>
        <Modal.Footer>
          {sync.status === 'idle' && (
            <>
              <Button variant="secondary" onClick={closeSync}>Cancel</Button>
              <Button variant="primary" onClick={runSync}>▶ Run Sync</Button>
            </>
          )}
          {isRunning && (
            <Button variant="outline-danger" size="sm"
              onClick={() => { abortRef.current = true; setSync(s => ({ ...s, status: 'idle' })); }}>
              ✕ Cancel
            </Button>
          )}
          {(isDone || sync.status === 'error') && (
            <Button variant="secondary" onClick={closeSync}>Close</Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

